/**
 * Bounded self-recovery for a view whose load failed with a transient error.
 *
 * A WorkQueue 503 (or a network failure) is short-lived, but a view that settled into
 * that error kept showing it until its regular refresh came round or the user changed a
 * filter. With this `refetchInterval`, the view asks again every
 * TRANSIENT_ERROR_REFETCH_MS while it is in a transient error, so the error clears soon
 * after the API recovers, with no user action and no remount (LIIP-1009 contract:
 * "transient self-clearing; cleared after success; WorkQueue 503 transient").
 *
 * Bounds (values in transientErrorConstants.ts):
 * - Only a 5xx or a network failure (`isTransientError`). A 401 waits for the host's new
 *   token (setAuthToken invalidates every query) and a 403 is a refusal, so neither is
 *   repeated here.
 * - TRANSIENT_ERROR_MAX_REFETCHES quick repeats (TRANSIENT_ERROR_REFETCH_MS) in each
 *   outage window, then slow repeats (TRANSIENT_ERROR_BACKOFF_MS).
 * - TRANSIENT_ERROR_STOP_AFTER_MS after the first failure of the outage the quick and slow
 *   repeats stop. The stop is time-based (window start + TRANSIENT_ERROR_STOP_AFTER_MS), so it
 *   holds whether or not React Query evaluated refetchInterval at that time: a hidden tab
 *   (no interval timer), an unmount and remount, a query with or without data. After the
 *   stop the view asks again only at the slow heartbeat (TRANSIENT_ERROR_HEARTBEAT_MS, not
 *   capped by the regular interval, so its jitter holds), on the wake events and on Retry,
 *   so a focused tab still recovers. A failure after the stop does not open a new window:
 *   the quick repeats never restart on their own. A success ends the outage, and so does a
 *   non-transient error (a 401 before the host's new token): the next transient failure
 *   opens a new window with new quick repeats.
 * - The view also re-checks on visibilitychange, window focus and the browser `online`
 *   event (useTransientErrorWake), and the error state keeps its Retry control.
 * - The host hears about each outage window twice at most: the failure that opens it, and
 *   the first failure after the stop (so a long outage does not go silent, PR #740 review
 *   item 8). The other repeats, the heartbeats and the wakes are not reported (items 4, 5).
 *   The reporting check (isInTransientOutage) does not depend on the order in which React
 *   Query evaluates refetchInterval (at fetch start for a query that holds data, and on the
 *   error) and runs the query function or the cache onError.
 * - Every interval above is jittered: a random 50-100% of its value
 *   (TRANSIENT_ERROR_JITTER_RATIO), so open tabs do not repeat in lockstep. The draw is
 *   fixed per failure, because React Query restarts the timer whenever the returned
 *   interval changes (PR #740 review item 6).
 * - A query with this refetchInterval sets `retry: false` (PR #740 review item 9): a
 *   transient error is asked again by the jittered repeats, one request per repeat (a React
 *   Query retry would send 2-5 requests per repeat); a 401 waits for the host's new token
 *   (setAuthToken refetches once) and a 403 is final, so retrying them adds nothing.
 *
 * Same behavior and bounds as the RevenueQueue helper (rms-queue hooks/transientErrorRefetch.ts);
 * the two packages share no code, so each keeps its own copy.
 */
import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { isTransientError } from "../services/apiError";
import {
  TRANSIENT_ERROR_BACKOFF_MS,
  TRANSIENT_ERROR_HEARTBEAT_MS,
  TRANSIENT_ERROR_JITTER_RATIO,
  TRANSIENT_ERROR_MAX_REFETCHES,
  TRANSIENT_ERROR_REFETCH_MS,
  TRANSIENT_ERROR_STOP_AFTER_MS,
} from "./transientErrorConstants";

/** The part of a React Query `Query` this helper reads. */
interface QueryWithState {
  state: {
    status: "pending" | "error" | "success";
    error: unknown;
    errorUpdateCount: number;
  };
}

/**
 * The current outage window of a query in a transient error.
 * - startedAt: time of the failure that opened the window, on the monotonic clock
 *   (performance.now), so a wall-clock change neither ends nor extends it. The window stops
 *   at startedAt + TRANSIENT_ERROR_STOP_AFTER_MS.
 * - errorsBefore: the query's errorUpdateCount before that failure (errorUpdateCount never
 *   resets), so the failure that opened the window is `errorsBefore + 1`.
 * - reportedAfterStop: the errorUpdateCount of the failure after the stop that is reported
 *   to the host (the only one), once a fetch after the stop has claimed it.
 * - jitter: the jitter factor drawn for the failure with errorUpdateCount `errors`.
 */
interface OutageWindow {
  startedAt: number;
  errorsBefore: number;
  reportedAfterStop?: number;
  jitter?: { errors: number; factor: number };
}

const outages = new WeakMap<object, OutageWindow>();

/**
 * Use as `refetchInterval: (query) => transientErrorRefetchInterval(query, regular)`.
 *
 * @param regularIntervalMs the interval the view uses when it is not in a transient
 *   error (`false` or 0 for none).
 */
export function transientErrorRefetchInterval(
  query: QueryWithState,
  regularIntervalMs: number | false
): number | false {
  const { status, error, errorUpdateCount } = query.state;

  if (status === "success" || (status === "error" && !isTransientError(error))) {
    // A success ends the outage. A non-transient error is not repeated here (a 401 waits
    // for the host's new token, a 403 is a refusal) and ends it too: the next transient
    // failure starts a new window with a new allowance of quick repeats.
    outages.delete(query);
    return regularIntervalMs;
  }
  if (status !== "error") {
    return regularIntervalMs;
  }

  let outage = outages.get(query);
  if (!outage) {
    // The first transient failure: a new window.
    outage = { startedAt: performance.now(), errorsBefore: errorUpdateCount - 1 };
    outages.set(query, outage);
  }
  if (isStopped(outage)) {
    // Stopped: keep the window until a success (deleting it would open a new one on the next
    // evaluation and restart the quick repeats) and ask again only at the heartbeat. Not
    // capped by a shorter regular interval: the heartbeat is the bound after the stop.
    return jittered(outage, errorUpdateCount, TRANSIENT_ERROR_HEARTBEAT_MS);
  }

  const failuresInOutage = errorUpdateCount - outage.errorsBefore;
  const repeatMs =
    failuresInOutage > TRANSIENT_ERROR_MAX_REFETCHES ? TRANSIENT_ERROR_BACKOFF_MS : TRANSIENT_ERROR_REFETCH_MS;
  return capByRegular(jittered(outage, errorUpdateCount, repeatMs), regularIntervalMs);
}

/** Whether the window has reached its time-based stop. */
function isStopped(outage: OutageWindow): boolean {
  return performance.now() - outage.startedAt >= TRANSIENT_ERROR_STOP_AFTER_MS;
}

/**
 * Whether the failure with errorUpdateCount `failure` is the one failure after the stop that
 * is reported. The first caller after the stop claims it; asking again for the same failure
 * gives the same answer, so a fetch that was cancelled before it failed leaves the claim to
 * the next fetch.
 */
function claimReportAfterStop(outage: OutageWindow, failure: number): boolean {
  if (outage.reportedAfterStop !== undefined && outage.reportedAfterStop !== failure) return false;
  outage.reportedAfterStop = failure;
  return true;
}

/**
 * `intervalMs` times a random factor in (1 - TRANSIENT_ERROR_JITTER_RATIO, 1]. Never more than
 * `intervalMs`, so a test or an e2e spec that waits the full constant still sees the fetch.
 * One draw per failure: a re-evaluation without a new failure returns the same interval.
 */
function jittered(outage: OutageWindow, errorUpdateCount: number, intervalMs: number): number {
  if (outage.jitter?.errors !== errorUpdateCount) {
    outage.jitter = { errors: errorUpdateCount, factor: 1 - TRANSIENT_ERROR_JITTER_RATIO * Math.random() };
  }
  return Math.round(intervalMs * outage.jitter.factor);
}

/** Never wait longer than a shorter regular interval; never stop on `false` or 0. */
function capByRegular(intervalMs: number, regularIntervalMs: number | false): number {
  return typeof regularIntervalMs === "number" && regularIntervalMs > 0
    ? Math.min(intervalMs, regularIntervalMs)
    : intervalMs;
}

/**
 * Whether a 5xx of the fetch that is starting now for the query with this key is inside an
 * outage the host already heard about, so it is not reported again.
 *
 * A query function that reports a 5xx to the host (onError) passes
 * `reportServerError: !isInTransientOutage(client, queryKey)`, once per fetch, so each outage
 * window is reported by the failure that opens it and by the first failure after the stop,
 * and by no other repeat, heartbeat or wake (LIIP-1036; contract C12; PR #740 review items
 * 5 and 8):
 * - no window (healthy, or the last failure was a 401/403): false, the failure opens one;
 * - a window before its stop: true;
 * - a window after its stop: false for the first fetch after the stop, true after that until
 *   a success. The stop is time-based, so this holds also when no refetchInterval
 *   evaluation ran since the stop (hidden tab, remount).
 * The query function runs before the failure, so the failure of this fetch will have
 * errorUpdateCount + 1.
 */
export function isInTransientOutage(client: QueryClient, queryKey: QueryKey): boolean {
  const query = client.getQueryCache().find({ queryKey, exact: true });
  if (query === undefined) return false;
  const outage = outages.get(query);
  if (outage === undefined) return false;
  if (!isStopped(outage)) return true;
  return !claimReportAfterStop(outage, query.state.errorUpdateCount + 1);
}
